function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { breakpoints } from '../../styles';
import combineRefs from '../../utilities/combine-refs';
import { useResizeObserver } from './container-query.hook';
var propTypes = {
  /** Component that will be measured on resize. */
  children: PropTypes.node,
  /** Callback function for when the size of the component changes. */
  onContainerSize: PropTypes.func
};
var defaultProps = {
  children: /*#__PURE__*/React.createElement(React.Fragment, null),
  onContainerSize: function onContainerSize() {}
};
export var getBreakpoint = function getBreakpoint(width) {
  /** Get breakpoint */
  var breakpoint = 'sm';
  for (var _i = 0, _Object$entries = Object.entries(breakpoints); _i < _Object$entries.length; _i++) {
    var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
      key = _Object$entries$_i[0],
      value = _Object$entries$_i[1];
    if (width >= value[0]) {
      breakpoint = key;
    }
  }
  return breakpoint;
};
var getViewportSize = function getViewportSize() {
  var vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
  var vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
  return {
    width: vw,
    height: vh,
    breakpoint: getBreakpoint(vw)
  };
};
export var getSizeWithProps = function getSizeWithProps(_ref) {
  var height = _ref.height,
    width = _ref.width;
  /** */
  var breakpoint = getBreakpoint(width);
  var viewportSize = getViewportSize();
  return {
    breakpoint: breakpoint,
    height: height,
    width: width,
    viewportSize: viewportSize
  };
};

/**
 * @ignore @description
 * ## ContainerQuery Component
 * Wrap the ContainerQuery component around an element to measure it's size, (width only), and watch for changes in size. A prop called containersize will be passed to the wrapped element, which can then be used for queries in styled components built in Emotion.
 *
 * @ignore @see Mesh: [ContainerQuery Docs](https://mesh-component-library.apps.csts-k8cnp-ro-1.r-caas.allstate.com/?path=/story/containers-containerquery--default)
 *
 * @ignore @example
 * ```jsx
 * import { ContainerQuery } from 'mesh-component-library';
 *
 * <ContainerQuery {...props}>
 *  {children}
 * </ContainerQuery>
 * ```
 */

var ContainerQuery = function ContainerQuery(props) {
  var children = props.children,
    onContainerSize = props.onContainerSize;
  var nodeRefs = useRef([]);
  var _useState = useState({
      breakpoint: 'sm',
      height: undefined,
      width: undefined,
      viewportSize: {}
    }),
    _useState2 = _slicedToArray(_useState, 2),
    size = _useState2[0],
    setSize = _useState2[1];
  var _useResizeObserver = useResizeObserver(),
    observe = _useResizeObserver.observe,
    unobserve = _useResizeObserver.unobserve;
  var onRefSet = useCallback(function (node) {
    if (!node) {
      return;
    }
    observe(node, function (resp) {
      /** */
      var width = resp.width,
        height = resp.height;
      var sizeWithProps = getSizeWithProps({
        height: height,
        width: width
      });
      setSize(sizeWithProps);
    });
    nodeRefs.current.push(node);
  }, []);
  useLayoutEffect(function () {
    // get size on first render, before paint
    setSize(getSizeWithProps({}));
    // stop observing
    return function () {
      nodeRefs.current.forEach(function (el) {
        unobserve(el);
      });
      nodeRefs.current = [];
    };
  }, []);
  useEffect(function () {
    onContainerSize(size);
  }, [size]);
  return React.Children.map(children, function (child) {
    return child ? /*#__PURE__*/React.cloneElement(child, {
      containersize: size,
      /** Don't lose the child's original ref */
      ref: combineRefs(onRefSet, child.ref)
    }) : null;
  });
};
ContainerQuery.propTypes = propTypes;
ContainerQuery.defaultProps = defaultProps;
export default ContainerQuery;